import iconsPng from '@/assets/img/png/icons';
import CustomButton from '@/components/atoms/CustomButton';
import ModalContainer from '@/components/molecules/ModalContainer';
import { LOCATION_STATE_DEFAULT_VALUE } from '@/constants';
import { FORM_FIELD_WIDTH } from '@/constants/dimensions';
import useUSStates from '@/hooks/meta/useUSStates';
import useUpdateLocation from '@/hooks/user/useUpdateLocation';
import colors from '@/theme/colors';
import { TUser, TUserLocation } from '@/types/User.types';
import { HStack, Image, Input, Select, Text, VStack } from '@chakra-ui/react';
import React from 'react';
import { useForm } from 'react-hook-form';

interface Props {
  isLocationModalVisible: boolean;
  setIsLocationModalVisible: (_: boolean) => void;
  onSuccess: (updatedUser: TUser) => void;
}

const LABEL_INPUT_VERTICAL_GAP = '20px';

const UserLocationUpdateModal = ({ isLocationModalVisible, setIsLocationModalVisible, onSuccess }: Props) => {
  const { data: USStates } = useUSStates();

  const { mutate: updateLocation, isPending: isLocationUpdatePending } = useUpdateLocation((user: TUser) => {
    onSuccess(user);
  });

  const onClickUpdate = () => {
    const values = getValues();
    updateLocation(values);
  };

  const { register, watch, formState, getValues } = useForm<TUserLocation>({
    mode: 'all',
    reValidateMode: 'onChange',
  });
  const { isValid: isValidInputFields, errors, touchedFields } = formState;

  const locationStateValue = watch('state');
  const locationStateInvalid = locationStateValue === LOCATION_STATE_DEFAULT_VALUE || !locationStateValue;

  return (
    <ModalContainer
      isOpen={isLocationModalVisible}
      onClose={() => {
        setIsLocationModalVisible(false);
      }}
      isBlur={false}
      width={'620px'}
      backgroundColor={'background.lightBlue'}
    >
      <Text variant={'loraTitle'} textAlign={'center'} fontSize={'22px'} marginTop={'12px'}>
        Update your Location
      </Text>
      <VStack marginBottom={'36px'} alignItems={'center'} marginTop={'36px'}>
        <VStack gap={'40px'} paddingX={'100px'}>
          <Image alt="ok-hand" src={iconsPng.locationPin} width={100} height={100} />
          <Text variant={'urbanistSemiBoldLarge'} color={'text.mediumBlue'}>
            We use your location to connect you with nearby therapists, ensuring convenient access to support. This
            helps us match you with professionals available for either telehealth or in-person sessions based on your
            preference and location, giving you flexibility in how you receive care.
          </Text>

          <VStack alignItems={'center'} width={'100%'}>
            <HStack spacing={'20px'} minWidth={'100%'}>
              <VStack flex={1} alignItems={'flex-start'} gap={LABEL_INPUT_VERTICAL_GAP}>
                <Text variant={'urbanistBoldLarge'}>Location</Text>
                <HStack width={'100%'}>
                  <Input
                    placeholder="City"
                    className="custom-input"
                    _placeholder={{ color: 'text.mediumGray', fontWeight: '600', fontSize: 12 }}
                    color={'text.darkGray'}
                    isInvalid={!!errors?.city}
                    {...register('city', {
                      required: { message: 'City is required', value: true },
                      minLength: { value: 2, message: 'City must have at least 2 characters' },
                      pattern: { value: /^[a-zA-Z\s]*$/, message: 'City can only contain letters and spaces' },
                    })}
                    data-state={touchedFields.city ? (errors.city ? 'invalid' : 'valid') : ''}
                  />

                  <Input
                    placeholder="ZIP Code"
                    className="custom-input"
                    _placeholder={{ color: 'text.mediumGray', fontWeight: '600', fontSize: 13 }}
                    color={'text.darkGray'}
                    isInvalid={!!errors?.zip}
                    {...register('zip', {
                      required: { message: 'ZIP Code is required', value: true },
                      pattern: { value: /^\d{5}(-\d{4})?(?!-)$/, message: 'Invalid ZIP Code' },
                    })}
                    minWidth={'70px'}
                    data-state={touchedFields.zip ? (errors.zip ? 'invalid' : 'valid') : ''}
                  />
                </HStack>
              </VStack>
            </HStack>
            <Text variant={'error'}>{errors?.city?.message || errors?.zip?.message || ''}</Text>

            <HStack width={'100%'} spacing={'10px'}>
              <VStack alignItems={'flex-start'} width={'100%'} gap={LABEL_INPUT_VERTICAL_GAP}>
                <HStack minWidth={'100%'} width={'100%'}>
                  <Select
                    minWidth={'100%'}
                    className="custom-input custom-select-padding"
                    defaultValue={LOCATION_STATE_DEFAULT_VALUE}
                    isInvalid={touchedFields.state && locationStateInvalid}
                    style={
                      locationStateInvalid
                        ? {
                            color: colors.text.mediumGray,
                            fontSize: '14px',
                            borderColor: touchedFields.state ? colors.extra.lightRed : '#e2e8f0',
                          }
                        : {}
                    }
                    {...register('state', {
                      required: { message: 'State is required', value: true },
                      validate: (value: string) =>
                        value === LOCATION_STATE_DEFAULT_VALUE ? 'State is required' : true,
                    })}
                    data-state={touchedFields.state ? (locationStateInvalid ? 'invalid' : 'valid') : ''}
                  >
                    <option disabled>{LOCATION_STATE_DEFAULT_VALUE}</option>
                    {(USStates ?? []).map((USState) => (
                      <option key={USState.label} value={USState.code}>
                        {USState.label}
                      </option>
                    ))}
                  </Select>
                </HStack>
              </VStack>
            </HStack>
            <Text variant={'error'}>{errors?.state?.message || ''}</Text>
          </VStack>

          <CustomButton
            width={FORM_FIELD_WIDTH}
            height={'54px'}
            onClick={onClickUpdate}
            backgroundColor="secondary.500"
            labelColor="extra.white"
            label="Update"
            disabled={!isValidInputFields}
            isLoading={isLocationUpdatePending}
          />
        </VStack>
      </VStack>
    </ModalContainer>
  );
};

export default UserLocationUpdateModal;
